<script setup lang="ts">

import { LinkTextPrimary, OrderRelationLink } from '@shared/ui/links'
import { BadgeCustomInvoice, BadgeOrderStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'

import OrderRowActions from './OrderRowActions.vue'
import { useTableOrders } from '../model/table-orders.hook'

defineOptions({
  name: 'TableOrders',
})

withDefaults(defineProps<{
  /** Off on the customer's own Orders tab, where every row is that customer; that tab keeps the legacy column widths. */
  withCustomer?: boolean
}>(), {
  withCustomer: true,
})

const {
  orders,
  isLoading,
  hasOrders,
  meta,
  perPageOptions,
  emptyCell,
  getTypeLabel,
  getInstallment,
  getCustomerName,
  getDisciplineLabel,
  getPackage,
  getPace,
  getTotal,
  getSucceededAt,
  getOrderUrl,
  getSubscriptionPath,
  getCustomerUrl,
  getKidUrl,
  getDisciplineUrl,
  setChangePage,
  setChangePerPage,
} = useTableOrders()
</script>

<template>
  <div :class="['table-orders', { 'table-orders--customer-tab': !withCustomer }]">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh class="table-orders__id-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              #
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Type
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Subscription #
            </AppText>
          </AppTh>
          <AppTh v-if="withCustomer">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Customer
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Kid
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Discipline
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Package
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Pace
            </AppText>
          </AppTh>
          <AppTh class="table-orders__status-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh :class="withCustomer ? 'text-right' : 'table-orders__total-cell'">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Total amount
            </AppText>
          </AppTh>
          <AppTh class="table-orders__succeeded-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Succeeded at
            </AppText>
          </AppTh>
          <AppTh class="table-orders__actions-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasOrders"
        #body
      >
        <AppTr
          v-for="order in orders"
          :key="order.id"
        >
          <AppTd class="medium">
            <div class="table-orders__id">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ order.id }}
              </AppText>
              <BadgeCustomInvoice v-if="order.has_custom_invoice" />
            </div>
          </AppTd>
          <AppTd class="medium table-orders__nowrap">
            <div class="table-orders__type">
              <LinkTextPrimary :href="getOrderUrl(order.id)">
                {{ getTypeLabel(order) }}
              </LinkTextPrimary>
              <AppText
                v-if="getInstallment(order)"
                name="body1"
                color="slate-900"
              >
                {{ getInstallment(order) }}
              </AppText>
            </div>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="order.subscription"
              :href="getSubscriptionPath(order.subscription.id)"
            >
              {{ order.subscription.id }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd
            v-if="withCustomer"
            class="medium"
          >
            <OrderRelationLink
              v-if="order.customer"
              :href="getCustomerUrl(order.customer.id)"
              :text="getCustomerName(order.customer)"
              :notes="order.customer.notes"
            />
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <div
              v-if="order.disciplines.length"
              class="table-orders__list"
            >
              <template
                v-for="(discipline, index) in order.disciplines"
                :key="discipline.id"
              >
                <OrderRelationLink
                  v-if="discipline.kid"
                  :href="getKidUrl(discipline.kid.id)"
                  :text="discipline.kid.name"
                  :notes="discipline.kid.notes"
                />
                <AppText
                  v-else
                  name="body1"
                  color="slate-900"
                >
                  {{ emptyCell }}
                </AppText>
                <AppText
                  v-if="index < order.disciplines.length - 1"
                  name="body1"
                  color="slate-900"
                >
                  ,
                </AppText>
              </template>
            </div>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <div
              v-if="order.disciplines.length"
              class="table-orders__list"
            >
              <template
                v-for="(discipline, index) in order.disciplines"
                :key="discipline.id"
              >
                <OrderRelationLink
                  :href="getDisciplineUrl(discipline.id)"
                  :text="getDisciplineLabel(discipline)"
                  :notes="discipline.notes"
                />
                <AppText
                  v-if="index < order.disciplines.length - 1"
                  name="body1"
                  color="slate-900"
                >
                  ,
                </AppText>
              </template>
            </div>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getPackage(order) }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-orders__nowrap">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getPace(order) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <BadgeOrderStatus :status="order.status" />
          </AppTd>
          <AppTd class="medium text-right table-orders__nowrap">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getTotal(order) }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-orders__nowrap">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getSucceededAt(order) }}
            </AppText>
          </AppTd>
          <AppTd class="zero-padding table-orders__actions-cell">
            <OrderRowActions
              :order="order"
              :with-customer="withCustomer"
            />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableOrders.scss" />
